import { forwardRef } from 'react';

import { ProgressActivity } from '@/components/ui/Icons';
import { Flex } from '@/new_src/view/components/Flex';

import type { PolymorphicComponentProps, PolymorphicRef } from '../types';
import type { Variant } from './color';
import { VARIANT_MAP } from './color';
import type { Size } from './size';
import { SIZE_MAP } from './size';

import { styled } from 'styled-components';

type _ButtonProps = {
  size: Size;
  width?: string;
  variant: Variant;
  children: React.ReactNode;
  isLoading?: boolean;
  isActive?: boolean;
  // TODO: Button을 styled(Button)으로 감싸면 polymorphic 제네릭이 풀리면서
  // 아래 콜백 매개변수의 contextual typing이 손실됨. 그 결과 호출부에서
  // `renderLeftIcon={({ width, height }: { width: number; height: number }) => ...}`처럼
  // 매개변수 타입을 명시해야 noImplicitAny: true에서 통과 (예: AddLevelButton, VendorDropdownButton).
  renderLeftIcon?: ({ width, height }: { width: number; height: number }) => React.ReactNode;
  renderRightIcon?: ({ width, height }: { width: number; height: number }) => React.ReactNode;
};

type ButtonProps<T extends React.ElementType> = PolymorphicComponentProps<T, _ButtonProps>;

// TODO: ButtonComponent type annotation 에러
type ButtonComponent = <T extends React.ElementType = 'button'>(props: ButtonProps<T>) => React.ReactElement;

// TODO: export를 하지 않으면 string literal 파싱이 제대로 되지 않음
// TODO: styled로 감쌌을 때 타입 에러 나지 않음
export const Button: ButtonComponent = forwardRef(
  // @ts-expect-error: 타입 에러
  <T extends React.ElementType>(
    {
      as,
      size,
      width,
      variant,
      children,
      isLoading,
      isActive,
      renderLeftIcon,
      renderRightIcon,
      ...props
    }: ButtonProps<T>,
    ref: PolymorphicRef<T>['ref']
  ) => {
    const Element = as || 'button';

    return (
      <Container
        as={Element}
        ref={ref}
        $width={width}
        $size={size}
        $variant={variant}
        $isActive={isActive}
        $hasLeft={!isLoading && !!renderLeftIcon}
        $hasRight={!isLoading && !!renderRightIcon}
        $isLoading={isLoading}
        $isDisabled={props.disabled}
        disabled={isLoading || props.disabled}
        {...props}
      >
        {isLoading && (
          <Flex pos="absolute" align="center" justify="center" top="50%" left="50%" transform="translate(-50%, -50%)">
            <ProgressActivity
              width={SIZE_MAP[size]['iconSize']}
              height={SIZE_MAP[size]['iconSize']}
              // - disabled={false}, isLoading={true}: 원래 텍스트 색상 (예: 빨간색)
              // - disabled={true}, isLoading={true}: disabled 텍스트 색상 (회색)
              fill={props.disabled ? VARIANT_MAP[variant]['text']['disabled'] : VARIANT_MAP[variant]['text']['default']}
            />
          </Flex>
        )}
        <Flex gap={SIZE_MAP[size]['gap']} h="100%" justify="center" align="center" opacity={isLoading ? 0 : 1}>
          {renderLeftIcon && renderLeftIcon({ width: SIZE_MAP[size]['iconSize'], height: SIZE_MAP[size]['iconSize'] })}
          {children}
          {renderRightIcon &&
            renderRightIcon({ width: SIZE_MAP[size]['iconSize'], height: SIZE_MAP[size]['iconSize'] })}
        </Flex>
      </Container>
    );
  }
);

const isFilled = (variant: Variant) => {
  return variant === 'blue-filled' || variant === 'red-filled';
};

const Container = styled.button<{
  $width: string | undefined;
  $size: keyof typeof SIZE_MAP;
  $variant: Variant;
  $isActive?: boolean;
  $hasLeft: boolean;
  $hasRight: boolean;
  $isLoading?: boolean;
  $isDisabled?: boolean;
}>`
  position: relative;
  min-width: fit-content;
  display: inline-block;
  width: ${({ $width }) => ($width ? `${$width}` : 'auto')};
  height: ${({ $size }) => `${SIZE_MAP[$size]['height']}px`};
  border-radius: 4px;
  padding-top: 0px;
  padding-bottom: 0px;
  padding-left: ${({ $size, $hasLeft }) =>
    $hasLeft ? `${SIZE_MAP[$size]['iconPadding']}px` : `${SIZE_MAP[$size]['padding']}px`};
  padding-right: ${({ $size, $hasRight }) =>
    $hasRight ? `${SIZE_MAP[$size]['iconPadding']}px` : `${SIZE_MAP[$size]['padding']}px`};
  color: ${({ $variant }) => VARIANT_MAP[$variant]['text']['default']};
  font-weight: ${({ $size, $variant }) => (isFilled($variant) ? 700 : SIZE_MAP[$size]['fontWeight'])};
  font-size: ${({ $size }) => `${SIZE_MAP[$size]['fontSize']}px`};
  line-height: 160%;
  background-color: ${({ $isActive, $variant }) =>
    $isActive
      ? VARIANT_MAP[$variant]['backgroundColor']['pressed']
      : VARIANT_MAP[$variant]['backgroundColor']['default']};
  border: 0;
  box-sizing: border-box;
  box-shadow: ${({ $variant }) =>
    VARIANT_MAP[$variant]['border'] ? `inset 0 0 0 1px ${VARIANT_MAP[$variant]['border']['default']}` : undefined};

  &:hover {
    background-color: ${({ $isActive, $variant }) =>
      $isActive
        ? VARIANT_MAP[$variant]['backgroundColor']['pressed']
        : VARIANT_MAP[$variant]['backgroundColor']['hover']};
  }

  &:focus-visible,
  &:active {
    outline: none;
    background-color: ${({ $variant }) => VARIANT_MAP[$variant]['backgroundColor']['pressed']};
  }

  /*
  1. isLoading={true}, disabled={false}: 원래 색상 유지 (빨간색 border)
  2. isLoading={false}, disabled={true}: disabled 색상 적용 (회색 border)
  3. isLoading={true}, disabled={true}: disabled 색상 적용 (회색 border)
  4. isLoading={false}, disabled={false}: 일반 상태 (원래 색상) 
  */
  &:disabled {
    color: ${({ $variant, $isLoading, $isDisabled }) =>
      $isLoading && !$isDisabled
        ? VARIANT_MAP[$variant]['text']['default']
        : VARIANT_MAP[$variant]['text']['disabled']};
    background-color: ${({ $variant, $isLoading, $isDisabled }) =>
      $isLoading && !$isDisabled
        ? VARIANT_MAP[$variant]['backgroundColor']['default']
        : VARIANT_MAP[$variant]['backgroundColor']['disabled']};
    box-shadow: ${({ $variant, $isLoading, $isDisabled }) =>
      VARIANT_MAP[$variant]['border']
        ? `inset 0 0 0 1px ${
            $isLoading && !$isDisabled
              ? VARIANT_MAP[$variant]['border']['default']
              : VARIANT_MAP[$variant]['border']['disabled']
          }`
        : undefined};
    cursor: ${({ $isLoading, $isDisabled }) => ($isLoading && !$isDisabled ? 'default' : 'not-allowed')};
  }
`;

export default Button;
